<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ isEdit ? '編輯平台' : '新增平台' }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="handleCancel" :disabled="loading">
          <i class="ri-close-line"></i>
          取消
        </button>
        <button type="button" class="btn btn-primary" :disabled="loading" @click="handleSubmit">
          <span v-if="loading">{{ isEdit ? '更新中...' : '建立中...' }}</span>
          <span v-else>{{ isEdit ? '更新平台' : '建立平台' }}</span>
        </button>
      </div>
    </div>

    <div class="page-container">
    <div class="create-platform-container">
    <div class="form-container">
      <form @submit.prevent="handleSubmit">
        <div class="form-section">
          <h3 class="section-title">基本資訊</h3>
          
          <div class="form-row">
            <div class="form-group">
              <label for="platformCode" class="required">平台代碼</label>
              <input
                id="platformCode"
                v-model="form.platformCode"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.platformCode }"
                placeholder="請輸入平台代碼（唯一識別碼）"
                :disabled="isEdit"
                required
              />
              <div v-if="errors.platformCode" class="invalid-feedback">
                {{ errors.platformCode }}
              </div>
              <p class="form-hint">平台代碼一旦建立後無法修改</p>
            </div>

            <div class="form-group">
              <label for="platformName" class="required">平台名稱</label>
              <input
                id="platformName"
                v-model="form.platformName"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.platformName }"
                placeholder="請輸入平台名稱"
                required
              />
              <div v-if="errors.platformName" class="invalid-feedback">
                {{ errors.platformName }}
              </div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="status">狀態</label>
              <select
                id="status"
                v-model="form.status"
                class="form-control"
              >
                <option value="active">啟用</option>
                <option value="inactive">停用</option>
              </select>
              <p class="form-hint">停用的平台將無法使用</p>
            </div>

            <div class="form-group">
              <label for="maxClients">客戶數上限</label>
              <input
                id="maxClients"
                v-model.number="form.maxClients"
                type="number"
                class="form-control"
                :class="{ 'is-invalid': errors.maxClients }"
                placeholder="請輸入客戶數上限（留空則不限制）"
                min="1"
              />
              <div v-if="errors.maxClients" class="invalid-feedback">
                {{ errors.maxClients }}
              </div>
              <p class="form-hint">平台可以管理的客戶數量上限，留空則不限制</p>
            </div>
          </div>
        </div>

        <div v-if="errorMessage" class="alert alert-danger">
          {{ errorMessage }}
        </div>

      </form>
    </div>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { platformManagementService } from '@/api/modules/platforms.js';
import { showSuccess, showError } from '@/utils/swal.js';

export default {
  name: 'CreatePlatform',
  setup() {
    const router = useRouter();
    const route = useRoute();
    const loading = ref(false);
    const errorMessage = ref('');
    const errors = reactive({});

    const isEdit = computed(() => {
      return !!route.params.id;
    });

    const form = reactive({
      platformCode: '',
      platformName: '',
      status: 'active',
      maxClients: null,
    });

    const validateForm = () => {
      // 清除之前的錯誤
      Object.keys(errors).forEach(key => delete errors[key]);
      errorMessage.value = '';

      let isValid = true;

      // 驗證平台代碼
      if (!isEdit.value && (!form.platformCode || form.platformCode.trim().length < 2)) {
        errors.platformCode = '平台代碼至少需要 2 個字元';
        isValid = false;
      }

      // 驗證平台名稱
      if (!form.platformName || form.platformName.trim().length === 0) {
        errors.platformName = '請輸入平台名稱';
        isValid = false;
      }

      // 驗證客戶數上限（如果提供了值，必須大於 0）
      if (form.maxClients !== undefined && form.maxClients !== null && form.maxClients !== '' && form.maxClients < 1) {
        errors.maxClients = '客戶數上限必須大於 0';
        isValid = false;
      }

      return isValid;
    };

    const fetchPlatform = async () => {
      if (!isEdit.value) {
        return;
      }

      loading.value = true;
      errorMessage.value = '';

      try {
        const response = await platformManagementService.getPlatform(route.params.id);

        if (response && response.success && response.data) {
          const platform = response.data;
          form.platformCode = platform.platformCode;
          form.platformName = platform.platformName;
          form.status = platform.status;
          form.maxClients = platform.maxClients || null;
        } else {
          await showError('獲取失敗', response && response.message || '獲取平台資料失敗');
          router.push({ name: 'PlatformManagement' });
        }
      } catch (error) {
        console.error('獲取平台資料失敗:', error);
        console.error('錯誤詳情:', {
          message: error.message,
          code: error.code,
          status: error.response?.status,
          data: error.response?.data,
          url: `/system/platforms/management/${route.params.id}`
        });
        
        let errorMessage = '獲取平台資料失敗，請稍後再試';
        if (error.code === 'NOT_FOUND') {
          errorMessage = '平台不存在或已被刪除';
        } else if (error.response?.status === 404) {
          errorMessage = '平台不存在或路由錯誤';
        } else if (error.response?.status === 403) {
          errorMessage = '您沒有權限查看此平台';
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        await showError('獲取失敗', errorMessage);
        router.push({ name: 'PlatformManagement' });
      } finally {
        loading.value = false;
      }
    };

    const handleSubmit = async () => {
      if (!validateForm()) {
        return;
      }

      loading.value = true;
      errorMessage.value = '';

      try {
        const platformData = {
          platformCode: form.platformCode.trim(),
          platformName: form.platformName.trim(),
          status: form.status,
        };
        
        // 只有當 maxClients 有值時才加入
        if (form.maxClients !== null && form.maxClients !== undefined && form.maxClients !== '') {
          platformData.maxClients = Number(form.maxClients);
        }

        let response;
        if (isEdit.value) {
          response = await platformManagementService.updatePlatform(route.params.id, platformData);
        } else {
          response = await platformManagementService.createPlatform(platformData);
        }

        // axios 攔截器已經返回 response.data，所以 response 就是 { success: true, data: {...}, message: '...' }
        if (response && response.success) {
          await showSuccess(
            isEdit.value ? '更新成功' : '新增成功',
            `平台「${platformData.platformName}」已成功${isEdit.value ? '更新' : '建立'}`
          );
          router.push({ name: 'PlatformManagement' });
        } else {
          // 處理 API 返回的錯誤
          let errorTitle = isEdit.value ? '更新失敗' : '新增失敗';
          let errorText = response?.message || `${isEdit.value ? '更新' : '新增'}平台失敗`;

          if (response?.error?.code === 'PLATFORM_CODE_EXISTS') {
            errors.platformCode = '平台代碼已存在';
            errorText = '平台代碼已存在';
          }
          await showError(errorTitle, errorText);
        }
      } catch (error) {
        console.error(`${isEdit.value ? '更新' : '新增'}平台失敗:`, error);
        console.error('錯誤詳情:', {
          message: error.message,
          code: error.code,
          status: error.response?.status,
          data: error.response?.data,
          url: isEdit.value 
            ? `/system/platforms/management/${route.params.id}` 
            : '/system/platforms/management'
        });
        
        let errorMessage = `${isEdit.value ? '更新' : '新增'}平台失敗，請稍後再試`;
        if (error.code === 'PLATFORM_CODE_EXISTS') {
          errorMessage = '平台代碼已存在，請使用其他代碼';
        } else if (error.code === 'NOT_FOUND') {
          errorMessage = '平台不存在或已被刪除';
        } else if (error.code === 'FORBIDDEN') {
          errorMessage = '您沒有權限執行此操作';
        } else if (error.response?.status === 401) {
          errorMessage = '未授權，請重新登入';
        } else if (error.response?.status === 403) {
          errorMessage = '您沒有權限執行此操作';
        } else if (error.response?.status === 400) {
          errorMessage = error.response?.data?.message || '輸入資料驗證失敗，請檢查輸入內容';
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        await showError(
          isEdit.value ? '更新失敗' : '新增失敗',
          errorMessage
        );
      } finally {
        loading.value = false;
      }
    };

    const handleCancel = () => {
      router.push({ name: 'PlatformManagement' });
    };

    onMounted(() => {
      if (isEdit.value) {
        fetchPlatform();
      }
    });

    return {
      form,
      errors,
      errorMessage,
      loading,
      isEdit,
      handleSubmit,
      handleCancel,
    };
  },
};
</script>

<style scoped>
.create-platform-container {
  padding: 0;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.form-container {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.form-section {
  margin-bottom: 32px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #f0f0f0;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
}

.form-group label.required::after {
  content: ' *';
  color: #dc3545;
}

.form-control {
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-control:disabled {
  background: #f5f5f5;
  cursor: not-allowed;
}

.form-control.is-invalid {
  border-color: #dc3545;
}

.invalid-feedback {
  color: #dc3545;
  font-size: 12px;
  margin-top: 4px;
}

.form-hint {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
  margin-bottom: 0;
}

.alert {
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 20px;
}

.alert-danger {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #f0f0f0;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background: #5a6268;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>
